import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { getHealth } from '@/api/health';
import { triggerPoll } from '@/api/poll';
import { Button } from '@/components/ui/button';
import { formatDistance } from '@/lib/format';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function SystemBadges() {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();

  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const pollMutation = useMutation({
    mutationFn: triggerPoll,
    onSuccess: (r) => {
      toast({
        title: t('system.pollStarted'),
        description: t('system.pollResult', { ...r }),
      });
    },
  });

  const transcribeOk = healthQuery.data?.transcribe_reachable ?? false;
  const lastPoll = healthQuery.data?.poll_state?.last_poll_at;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                transcribeOk
                  ? 'bg-emerald-500/10 text-emerald-700'
                  : 'bg-destructive/10 text-destructive'
              }`}
            >
              ● {transcribeOk ? t('system.transcribeOk') : t('system.transcribeDown')}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            {t('system.transcribeTip', { url: healthQuery.data?.transcribe_url ?? '—' })}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
              ●{' '}
              {lastPoll
                ? t('system.pollOk', { ago: formatDistance(lastPoll) })
                : t('system.pollNever')}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            {t('system.pollTip')}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => pollMutation.mutate()}
              disabled={pollMutation.isPending}
            >
              {t('system.runPollNow')}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            {t('system.runPollNowTip')}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
